import type { BhapticsContent } from "@/store/reducers/contents";

/**
 * 「How to Play」 버튼이 여는 주소.
 *
 * ## 왜 두 필드를 보나
 *
 * 가이드 주소가 **두 필드**에 나뉘어 들어온다. 어느 쪽이 채워지는지가 게임마다 다르다 —
 * prod 379개 실측(2026-09-01):
 *
 * ```
 * 둘 다            143
 * guideLink 만      25
 * guideSummary 만   15    ← 여기를 안 보면 가이드가 있는데 못 연다
 * 둘 다 없음       196
 * ```
 *
 * ## 우선순위 — `guideLink` 먼저 (2026-09-01 결정)
 *
 * **`guideLink` 가 정본이고 `guideSummary` 는 폴백이다.** prod 실측이 그 모양을 지지한다 —
 * `guideSummary` 158건 중 143건은 `guideLink` 와 겹치고, **단독은 15건**뿐이다.
 * 즉 폴백이 실제로 발동하는 자리가 그 15건이다.
 *
 * ```
 * guideSummary 158건   전부 bhaptics.com/docs/{uuid}  = 「플레이 방법」 문서
 * guideLink    168건   bhaptics.notion.site  98
 *                      github.com            45   ← 모드 릴리스·리드미
 *                      www.nexusmods.com     16   ← 모드 배포
 *                      기타(sidequest·thunderstore…) 9
 * ```
 *
 * 둘 다 있는 143건도 값이 **전부 다르다**(동일 0건). 예: `palworld` 는
 * `guideLink`=nexusmods 모드 페이지 · `guideSummary`=자사 docs.
 *
 * ⚠️ **대가를 적어 둔다.** `guideLink` 우선이므로 둘 다 가진 **143건은 자사 docs 로 가는
 * 경로가 상세에서 사라진다**(종전엔 「게임 정보」 카드 안 iframe 이 그 역할이었고, 이 PR 이
 * 그 iframe 을 걷었다 — 시안에 없고 실측 300×150 으로 못 쓸 크기였다). 「유실 0」이 아니다.
 * 대신 `guideSummary` 만 있는 15건은 종전에 버튼이 없었는데 이제 열린다.
 *
 * 대안(`guideSummary` 우선)은 유실이 0 이지만 143건의 목적지가 바뀌고, 그중 55건은
 * **모드 다운로드 페이지 → 문서**로 성격이 달라진다. 정본을 뒤집는 변경이라 하지 않았다.
 *
 * ## URL 검증이 필요하다
 *
 * admin 자유입력이라 URL 이 아닌 값이 온다 — dev `into-the-radius-vr` 의
 * `guideSummary` 가 `"222"` 다. 검증 없이 `href` 에 넣으면 현재 경로 기준 상대링크로
 * 해석돼 엉뚱한 곳으로 간다. 값이 URL 이 아니면 **버튼을 아예 그리지 않는다**
 * (`""` 반환 → 호출처가 falsy 로 거른다).
 *
 * ⚠️ `sanitize-media-links.ts` 와 규칙이 다르다. 저쪽은 `next/image` 라 `/` 로 시작하는
 * 내부 경로도 허용하는데, 여기는 **외부 문서로 나가는 링크**라 스킴이 있는 것만 받는다.
 */
const isHttpUrl = (v: unknown): v is string =>
  typeof v === "string" &&
  (v.trim().startsWith("http://") || v.trim().startsWith("https://"));

export const getGuideHref = (
  game: Pick<BhapticsContent, "guideLink" | "guideSummary">,
): string => {
  if (isHttpUrl(game.guideLink)) return game.guideLink.trim();
  if (isHttpUrl(game.guideSummary)) return game.guideSummary.trim();
  return "";
};
